import { ReactNode } from 'react'

export interface AvatarProps {
  /**
   * The name to display or generate initials from
   */
  name: string | null | undefined

  /**
   * Optional image URL
   */
  src?: string

  /**
   * Avatar size
   * @default 'medium'
   */
  size?: 'small' | 'medium' | 'large'

  /**
   * Avatar shape
   * - 'circle': Fully rounded (default)
   * - 'square': Slightly rounded corners (for badge printing, lists)
   * @default 'circle'
   */
  shape?: 'circle' | 'square'

  /**
   * Custom background color (overrides auto-generated color)
   */
  backgroundColor?: string

  /**
   * Custom text color
   * @default 'white'
   */
  textColor?: string

  /**
   * Additional CSS classes
   */
  className?: string

  /**
   * Click handler
   */
  onClick?: () => void
}

/**
 * Default avatar colors for generating consistent colors based on name
 */
const AVATAR_COLORS = [
  '#EB1964',
  '#4277BB',
  '#22C55E',
  '#F97316',
  '#8B5CF6',
  '#EC4899',
  '#14B8A6',
  '#F59E0B',
  '#6366F1',
  '#EF4444',
]

/**
 * Get a consistent color based on the name
 */
function normalizeAvatarName(name: string | null | undefined): string {
  return typeof name === 'string' ? name.trim() : ''
}

/**
 * Get a consistent color based on the name
 */
function getAvatarColor(name: string | null | undefined): string {
  const normalized = normalizeAvatarName(name)
  if (!normalized) return AVATAR_COLORS[0]

  const index = normalized.charCodeAt(0) % AVATAR_COLORS.length
  return AVATAR_COLORS[index]
}

/**
 * Get initials from a name (up to 2 characters)
 */
function getInitials(name: string | null | undefined): string {
  const normalized = normalizeAvatarName(name)
  if (!normalized) return '?'

  const parts = normalized.split(/\s+/)
  if (parts.length >= 2) {
    return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase()
  }
  return normalized.charAt(0).toUpperCase()
}

/**
 * Get size classes based on size and shape props
 * Square shape uses slightly larger sizes (for badge printing compatibility)
 */
function getSizeClasses(size: AvatarProps['size'], shape: AvatarProps['shape'] = 'circle'): { container: string; text: string } {
  if (shape === 'square') {
    // Badge printing sizes (slightly larger)
    switch (size) {
      case 'small':
        return { container: 'w-8 h-8', text: 'text-sm' }
      case 'large':
        return { container: 'w-12 h-12', text: 'text-lg' }
      case 'medium':
      default:
        return { container: 'w-10 h-10', text: 'text-base' }
    }
  }
  // Circle (default) sizes
  switch (size) {
    case 'small':
      return { container: 'w-6 h-6', text: 'text-[10px]' }
    case 'large':
      return { container: 'w-12 h-12', text: 'text-base' }
    case 'medium':
    default:
      return { container: 'w-8 h-8', text: 'text-xs' }
  }
}

/**
 * Avatar Component
 *
 * Displays a user avatar with initials or an image. Automatically generates
 * consistent background colors based on the user's name.
 *
 * @example
 * // Basic usage with name
 * <Avatar name="John Doe" />
 *
 * @example
 * // With image
 * <Avatar name="John Doe" src="/avatars/john.jpg" />
 *
 * @example
 * // Different sizes
 * <Avatar name="John Doe" size="small" />
 * <Avatar name="John Doe" size="large" />
 *
 * @example
 * // Square shape (for badge printing, lists)
 * <Avatar name="John Doe" shape="square" />
 *
 * @example
 * // Custom color
 * <Avatar name="John Doe" backgroundColor="#3B82F6" />
 */
export function Avatar({
  name,
  src,
  size = 'medium',
  shape = 'circle',
  backgroundColor,
  textColor = 'white',
  className = '',
  onClick,
}: AvatarProps) {
  const normalizedName = normalizeAvatarName(name)
  const displayName = normalizedName || 'Unknown'
  const sizeClasses = getSizeClasses(size, shape)
  const bgColor = backgroundColor || getAvatarColor(displayName)
  const initials = getInitials(normalizedName)

  const roundingClass = shape === 'square' ? 'rounded-sm' : 'rounded-full'
  const fontWeightClass = shape === 'square' ? 'font-normal' : 'font-medium'
  const baseClasses = `${sizeClasses.container} ${roundingClass} flex items-center justify-center ${fontWeightClass} ${sizeClasses.text}`
  const interactiveClasses = onClick ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''

  if (src) {
    return (
      <img
        src={src}
        alt={displayName}
        className={`${baseClasses} object-cover ${interactiveClasses} ${className}`}
        onClick={onClick}
      />
    )
  }

  return (
    <div
      className={`${baseClasses} ${interactiveClasses} ${className}`}
      style={{ backgroundColor: bgColor, color: textColor }}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      title={displayName}
      aria-label={displayName}
    >
      {initials}
    </div>
  )
}

export interface AvatarWithNameProps extends Omit<AvatarProps, 'onClick'> {
  /**
   * Optional subtitle (e.g., email, role)
   */
  subtitle?: string

  /**
   * Layout direction
   * @default 'horizontal'
   */
  layout?: 'horizontal' | 'vertical'

  /**
   * Custom content to render after the name
   */
  extra?: ReactNode

  /**
   * Click handler for the entire component
   */
  onClick?: () => void
}

/**
 * AvatarWithName Component
 *
 * Displays an avatar alongside the user's name and optional subtitle.
 *
 * @example
 * // Basic usage
 * <AvatarWithName name="John Doe" subtitle="john@example.com" />
 *
 * @example
 * // Vertical layout
 * <AvatarWithName name="John Doe" layout="vertical" />
 *
 * @example
 * // With extra content
 * <AvatarWithName
 *   name="John Doe"
 *   subtitle="Admin"
 *   extra={<Badge variant="success">Active</Badge>}
 * />
 */
export function AvatarWithName({
  name,
  subtitle,
  layout = 'horizontal',
  extra,
  onClick,
  ...avatarProps
}: AvatarWithNameProps) {
  const displayName = normalizeAvatarName(name) || 'Unknown'
  const isHorizontal = layout === 'horizontal'
  const containerClasses = isHorizontal ? 'flex items-center gap-3' : 'flex flex-col items-center gap-2'
  const textContainerClasses = isHorizontal ? '' : 'text-center'
  const interactiveClasses = onClick ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''

  return (
    <div
      className={`${containerClasses} ${interactiveClasses}`}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <Avatar name={name} {...avatarProps} />
      <div className={textContainerClasses}>
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-900">{displayName}</span>
          {extra}
        </div>
        {subtitle && (
          <span className="text-sm text-gray-500">{subtitle}</span>
        )}
      </div>
    </div>
  )
}

// Export utility functions for external use
export { getAvatarColor, getInitials, AVATAR_COLORS }
